import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, Injector, NgZone, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, catchError, filter, from, fromEvent, merge, switchMap, tap, timer } from 'rxjs';
import { reloadPage } from '@olv/ui-shared/utils/browser.utils';

const VERSION_URL = 'version.json';
const FIRST_CHECK_MS = 10_000;
const VERSION_CHECK_MS = 5 * 60_000;

@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private readonly injector = inject(Injector);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);

  private started = false;
  private loadedVersion?: string;
  private notifiedVersion?: string;
  private toastModule?: Promise<typeof import('@olv/ui-shared/ui-shared/toast/toast.service')>;

  start(): void {
    if (this.started) return;
    this.started = true;

    // Off the zone: polling must not hold the app unstable
    this.zone.runOutsideAngular(() => {
      const tabShown = fromEvent(this.document, 'visibilitychange').pipe(
        filter(() => this.document.visibilityState === 'visible'),
      );

      merge(timer(FIRST_CHECK_MS, VERSION_CHECK_MS), tabShown)
        .pipe(
          // Before a deploy removes it
          tap(() => this.loadToast().catch(() => undefined)),
          switchMap(() => this.fetchVersion()),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((version) => this.compare(version));
    });
  }

  notifyNewVersion(): void {
    this.loadToast()
      .then(({ ToastService }) =>
        this.zone.run(() =>
          this.injector.get(ToastService).info('Refresh to load the latest version.', 'A new version is available', {
            disableTimeOut: true,
            tapToDismiss: false,
            closeButton: true,
            action: { label: 'Refresh', run: reloadPage },
          }),
        ),
      )
      .catch(() => undefined);
  }

  private compare(version: string): void {
    this.loadedVersion ??= version;
    if (version === this.loadedVersion || version === this.notifiedVersion) return;

    this.notifiedVersion = version;
    this.notifyNewVersion();
  }

  private loadToast() {
    return (this.toastModule ??= import('@olv/ui-shared/ui-shared/toast/toast.service'));
  }

  // Skips the auth interceptors
  private fetchVersion(): Observable<string> {
    const request = fetch(VERSION_URL, { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then((body: { version?: unknown } | null) => (typeof body?.version === 'string' ? body.version : ''));

    return from(request).pipe(
      filter(Boolean),
      catchError(() => EMPTY),
    );
  }
}
